import React, { useContext, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { MapContext } from './mapContext'
import { FeatureCollection, Geometry } from 'geojson'
import { ThemeProvider, useTheme } from 'styled-components'
import { FeatureWithInfoAndStyle } from '~/packages/sentinel/statsAndGeoToFeatureCollection.util'
import { createPortal } from 'react-dom'

interface GeoJSONProps {
  geoJson: FeatureCollection<Geometry | null>
  idPropertyName?: string
  dataMapStyle?:
    | ((feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions)
    | google.maps.Data.StyleOptions
  renderInfoWindow?: (props: { feature: FeatureWithInfoAndStyle }) => JSX.Element
  onClick?: (event: google.maps.Data.MouseEvent) => void
}

export default function GeoJSON({
  geoJson,
  idPropertyName = 'id',
  dataMapStyle = (feature: google.maps.Data.Feature) => ({
    fillColor: feature.getProperty('fillColor') as string,
    fillOpacity: feature.getProperty('fillOpacity') as number,
    strokeColor: feature.getProperty('strokeColor') as string,
    strokeOpacity: feature.getProperty('strokeOpacity') as number,
    strokeWeight: feature.getProperty('strokeWeight') as number,
    zIndex: feature.getProperty('zIndex') as number,
  }),
  renderInfoWindow,
  onClick,
}: GeoJSONProps) {
  const mapContext = useContext(MapContext)
  const theme = useTheme()

  if (!mapContext) {
    throw new Error('GeoJson must be used within a Map')
  }

  const { map, google, mapType } = mapContext

  const [currentPopup, setCurrentPopup] = useState<{
    id: string
    node: HTMLElement
    feature: FeatureWithInfoAndStyle
    position: google.maps.LatLng | null
  } | null>(null)

  useEffect(() => {
    if (!map || !google) {
      return
    }

    const layer = new google.maps.Data()
    layer.addGeoJson(geoJson, { idPropertyName })

    layer.addListener('click', function (event: google.maps.Data.MouseEvent) {
      if (onClick) {
        onClick(event)
      }
      if (!renderInfoWindow) {
        return
      }

      // Get turf Feature
      const feature = geoJson.features.find(
        current =>
          current.properties?.[idPropertyName] === event.feature.getProperty(idPropertyName),
      ) as FeatureWithInfoAndStyle
      const featureWithPositionClicked = {
        ...feature,
        properties: {
          ...feature.properties,
          positionClicked: { lng: event.latLng?.lng() ?? 0, lat: event.latLng?.lat() ?? 0 },
        },
      }
      const div = document.createElement('div')
      div.className = 'prix-info-window'

      setCurrentPopup({
        id: Date.now().toString(),
        node: div,
        feature: featureWithPositionClicked,
        position: event.latLng,
      })
    })

    if (dataMapStyle) {
      layer.setStyle(dataMapStyle)
    }
    layer.setMap(map)

    return () => {
      layer.setMap(null)
    }
  }, [
    map,
    google,
    geoJson,
    idPropertyName,
    dataMapStyle,
    mapType,
    renderInfoWindow,
    theme,
    onClick,
  ])

  useEffect(() => {
    if (!currentPopup || !map || !google || !currentPopup.position) return

    const infoWindow = new google.maps.InfoWindow()

    infoWindow.setContent(currentPopup.node)
    infoWindow.setPosition(currentPopup.position)
    infoWindow.open(map)

    const closeListener = infoWindow.addListener('closeclick', () => {
      setCurrentPopup(null)
    })

    return () => {
      infoWindow.close()
      google.maps.event.removeListener(closeListener)
    }
  }, [currentPopup, map])

  return (
    currentPopup &&
    renderInfoWindow &&
    createPortal(renderInfoWindow({ feature: currentPopup.feature }), currentPopup.node)
  )
}
